import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, RouterProvider, useLocation } from 'react-router';
import { PageLoader } from './components/ui';
import { AdminLayout } from './layouts/AdminLayout';
import { AppLayout } from './layouts/AppLayout';
import { AuthLayout } from './layouts/AuthLayout';
import { PublicLayout } from './layouts/PublicLayout';
import { useAuth } from './lib/auth';
import { useOrg } from './lib/org';

const page = (loader: () => Promise<{ default: React.ComponentType }>) => {
  const Component = lazy(loader);
  return <Suspense fallback={<PageLoader />}><Component /></Suspense>;
};

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoader />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

function RequireWorkspace({ children }: { children: ReactNode }) {
  const { me, meLoading, orgId, ctxLoading } = useOrg();
  if (meLoading || (orgId && ctxLoading)) return <PageLoader />;
  if (me && me.organizations.length === 0) return <Navigate to="/onboarding" replace />;
  // The workspace is picked in an effect after /api/me loads; wait for it.
  if (me && !orgId) return <PageLoader />;
  return <>{children}</>;
}

function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { me, meLoading } = useOrg();
  if (meLoading) return <PageLoader />;
  if (!me?.platform_admin_role) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    children: [
      { path: '/', element: page(() => import('./pages/public/Landing')) },
      { path: '/pricing', element: page(() => import('./pages/public/Pricing')) },
      { path: '/features', element: page(() => import('./pages/public/Features')) },
      { path: '/industries', element: page(() => import('./pages/public/Industries')) },
      { path: '/integrations', element: page(() => import('./pages/public/IntegrationsInfo')) },
      { path: '/ai', element: page(() => import('./pages/public/AiFeatures')) },
      { path: '/help', element: page(() => import('./pages/public/HelpCenter')) },
      { path: '/help/:slug', element: page(() => import('./pages/public/HelpArticle')) },
      { path: '/contact', element: page(() => import('./pages/public/Contact')) },
      { path: '/terms', element: page(() => import('./pages/public/Terms')) },
      { path: '/privacy', element: page(() => import('./pages/public/Privacy')) },
      { path: '/data-deletion', element: page(() => import('./pages/public/DataDeletion')) },
    ],
  },
  {
    element: <AuthLayout />,
    children: [
      { path: '/login', element: page(() => import('./pages/auth/Login')) },
      { path: '/register', element: page(() => import('./pages/auth/Register')) },
      { path: '/forgot-password', element: page(() => import('./pages/auth/ForgotPassword')) },
      { path: '/reset-password', element: page(() => import('./pages/auth/ResetPassword')) },
      { path: '/invite/:token', element: page(() => import('./pages/auth/AcceptInvite')) },
    ],
  },
  { path: '/onboarding', element: <RequireAuth>{page(() => import('./pages/onboarding/Onboarding'))}</RequireAuth> },
  {
    path: '/app',
    element: <RequireAuth><RequireWorkspace><AppLayout /></RequireWorkspace></RequireAuth>,
    children: [
      { index: true, element: page(() => import('./pages/app/Dashboard')) },
      { path: 'leads', element: page(() => import('./pages/app/Leads')) },
      { path: 'leads/:id', element: page(() => import('./pages/app/LeadDetail')) },
      { path: 'contacts', element: page(() => import('./pages/app/Contacts')) },
      { path: 'contacts/:id', element: page(() => import('./pages/app/ContactDetail')) },
      { path: 'companies', element: page(() => import('./pages/app/Companies')) },
      { path: 'companies/:id', element: page(() => import('./pages/app/CompanyDetail')) },
      { path: 'deals', element: page(() => import('./pages/app/Deals')) },
      { path: 'deals/:id', element: page(() => import('./pages/app/DealDetail')) },
      { path: 'tasks', element: page(() => import('./pages/app/Tasks')) },
      { path: 'calendar', element: page(() => import('./pages/app/Calendar')) },
      { path: 'inbox', element: page(() => import('./pages/app/Inbox')) },
      { path: 'analytics', element: page(() => import('./pages/app/Analytics')) },
      { path: 'ai', element: page(() => import('./pages/app/Ai')) },
      { path: 'automations', element: page(() => import('./pages/app/Automations')) },
      { path: 'automations/:id', element: page(() => import('./pages/app/AutomationEditor')) },
      { path: 'imports', element: page(() => import('./pages/app/Imports')) },
      { path: 'team', element: page(() => import('./pages/app/Team')) },
      { path: 'notifications', element: page(() => import('./pages/app/Notifications')) },
      { path: 'support', element: page(() => import('./pages/app/Support')) },
      { path: 'settings', element: page(() => import('./pages/app/settings/SettingsLayout')),
        children: [
          { index: true, element: <Navigate to="general" replace /> },
          { path: 'general', element: page(() => import('./pages/app/settings/General')) },
          { path: 'pipelines', element: page(() => import('./pages/app/settings/Pipelines')) },
          { path: 'fields', element: page(() => import('./pages/app/settings/CustomFields')) },
          { path: 'tags', element: page(() => import('./pages/app/settings/TagsSources')) },
          { path: 'products', element: page(() => import('./pages/app/settings/Products')) },
          { path: 'integrations', element: page(() => import('./pages/app/settings/Integrations')) },
          { path: 'billing', element: page(() => import('./pages/app/settings/Billing')) },
          { path: 'branding', element: page(() => import('./pages/app/settings/Branding')) },
          { path: 'notifications', element: page(() => import('./pages/app/settings/NotificationSettings')) },
          { path: 'audit', element: page(() => import('./pages/app/settings/AuditLog')) },
          { path: 'data', element: page(() => import('./pages/app/settings/DataPrivacy')) },
        ] },
    ],
  },
  {
    path: '/admin',
    element: <RequireAuth><RequirePlatformAdmin><AdminLayout /></RequirePlatformAdmin></RequireAuth>,
    children: [
      { index: true, element: page(() => import('./pages/admin/Overview')) },
      { path: 'companies', element: page(() => import('./pages/admin/Companies')) },
      { path: 'companies/:id', element: page(() => import('./pages/admin/CompanyDetail')) },
      { path: 'users', element: page(() => import('./pages/admin/Users')) },
      { path: 'plans', element: page(() => import('./pages/admin/Plans')) },
      { path: 'flags', element: page(() => import('./pages/admin/Flags')) },
      { path: 'ai', element: page(() => import('./pages/admin/AiAdmin')) },
      { path: 'support', element: page(() => import('./pages/admin/SupportAdmin')) },
      { path: 'operations', element: page(() => import('./pages/admin/Operations')) },
      { path: 'backups', element: page(() => import('./pages/admin/Backups')) },
      { path: 'settings', element: page(() => import('./pages/admin/PlatformSettings')) },
    ],
  },
  { path: '*', element: <PublicLayout />, children: [{ path: '*', element: page(() => import('./pages/public/NotFound')) }] },
]);

export function App() {
  return <RouterProvider router={router} />;
}
